import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Package, ArrowRight, ShoppingBag, Sparkles } from 'lucide-react';
import Navigation from '@/components/Navigation';
import Footer from '@/components/Footer';
import SEOHead from '@/components/SEOHead';
import { GRADIENT_PRESETS, gradientStyle } from '@/components/admin/DiscountManager';

const API = process.env.REACT_APP_BACKEND_URL;

const categoryLabels = {
  apparel: 'Apparel',
  drinkware: 'Drinkware',
  bags: 'Bags',
  stationery: 'Stationery',
  bundles: 'Bundles',
};

function ProductCard({ product }) {
  const views = product.views || [];
  const firstView = views[0];
  const colors = product.color_variants || [];
  const imageUrl = (() => {
    const viewId = firstView?.id;
    if (viewId && colors[0]?.images?.[viewId]) return `${API}/api/files/${colors[0].images[viewId]}`;
    if (firstView?.image) return `${API}/api/files/${firstView.image}`;
    return null;
  })();
  const optionCount = product.sub_options?.reduce((sum, g) => sum + (g.options?.length || 0), 0) || 0;
  const isSoldOut = product.status === 'sold_out';

  const Wrapper = isSoldOut ? 'div' : Link;
  const wrapperProps = isSoldOut
    ? { 'data-testid': `product-card-${product.slug}`, className: 'group block bg-white dark:bg-[#111111] border border-slate-200 dark:border-white/5 rounded-2xl overflow-hidden opacity-75 cursor-not-allowed' }
    : { to: `/shop/${product.slug}`, 'data-testid': `product-card-${product.slug}`, className: 'group block bg-white dark:bg-[#111111] border border-slate-200 dark:border-white/5 rounded-2xl overflow-hidden hover:-translate-y-1 hover:shadow-xl hover:shadow-cyan-500/10 transition-[transform,box-shadow] duration-300' };

  return (
    <Wrapper {...wrapperProps}>
      <div className="aspect-square bg-white relative overflow-hidden">
        {imageUrl ? (
          <img src={imageUrl} alt={product.title} className={`w-full h-full object-contain p-6 ${isSoldOut ? 'grayscale' : 'group-hover:scale-105'} transition-transform duration-500`} />
        ) : (
          <div className="w-full h-full flex items-center justify-center">
            <Package className="w-12 h-12 text-slate-300 dark:text-slate-700" />
          </div>
        )}
        {/* Badges row — always top-left, side by side */}
        <div className="absolute top-2 left-2 right-2 flex items-center gap-1.5 z-10">
          {product.category && (
            <span className="text-[8px] font-bold uppercase tracking-widest text-[#00E5FF] bg-[#0A0A0A]/80 dark:bg-[#0A0A0A]/90 px-2 py-0.5 rounded-full backdrop-blur-sm">
              {categoryLabels[product.category] || product.category}
            </span>
          )}
          {isSoldOut && (
            <span className="text-[8px] font-bold uppercase tracking-widest text-white bg-rose-500 px-2 py-0.5 rounded-full" data-testid={`sold-out-badge-${product.slug}`}>
              Sold Out
            </span>
          )}
        </div>
      </div>
      <div className="p-4">
        <h3 className="font-['Outfit'] text-base font-semibold text-[#0A0A0A] dark:text-white group-hover:text-[#00E5FF] transition-colors leading-snug">
          {product.title}
        </h3>
        <div className="flex items-center justify-between mt-2">
          <span className={`text-lg font-bold ${isSoldOut ? 'text-slate-400 line-through' : 'text-[#0A0A0A] dark:text-white'}`}>
            From £{product.base_price.toFixed(2)}
          </span>
          {optionCount > 0 && (
            <span className="text-[10px] text-slate-500 dark:text-slate-500">{optionCount} options</span>
          )}
        </div>
        {colors.length > 0 && (
          <div className="flex items-center gap-1.5 mt-2.5" data-testid={`product-colors-${product.slug}`}>
            {colors.slice(0, 6).map((c, i) => (
              <div key={i} title={c.name} className="w-4 h-4 rounded-full border border-slate-300 dark:border-white/20 flex-shrink-0" style={{ backgroundColor: c.hex_code }} />
            ))}
            {colors.length > 6 && <span className="text-[9px] text-slate-400">+{colors.length - 6}</span>}
          </div>
        )}
        <div className="mt-3 flex items-center gap-1.5 text-xs font-medium text-[#00E5FF] opacity-0 group-hover:opacity-100 transition-opacity">
          {isSoldOut ? 'Sold Out' : <>Customise <ArrowRight className="w-3 h-3" /></>}
        </div>
      </div>
    </Wrapper>
  );
}

export default function ShopPage() {
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');

  const [testMode, setTestMode] = useState(false);
  const [saleBanner, setSaleBanner] = useState(null);

  useEffect(() => {
    fetch(`${API}/api/shop/products`)
      .then(res => res.json())
      .then(data => setProducts(data))
      .catch(err => console.error(err))
      .finally(() => setLoading(false));
    fetch(`${API}/api/shop/settings`).then(r => r.json()).then(d => setTestMode(!!d.test_mode)).catch(() => {});
    fetch(`${API}/api/shop/sale-banner`).then(r => r.json()).then(d => { if (d.active) setSaleBanner(d); }).catch(() => {});
  }, []);

  const categories = ['all', ...new Set(products.map(p => p.category).filter(Boolean))];
  const filtered = filter === 'all' ? products : products.filter(p => p.category === filter);

  return (
    <div className="min-h-screen bg-white dark:bg-[#0A0A0A] transition-colors duration-300 relative z-[1]">
      <SEOHead
        title="Custom Print Shop | Branded Merchandise &amp; Print On Demand | UI Designs"
        description="Customise premium branded products for your business. Hoodies, t-shirts, mugs, business cards and more with your logo. Print on demand in Bournemouth and across the UK."
        path="/shop"
        keywords="custom print shop, print on demand, branded merchandise Bournemouth, custom hoodies, branded mugs, company clothing"
      />
      <Navigation />

      {/* Test mode ribbon — sits visually below the fixed nav */}
      {testMode && (
        <div className="pt-16 md:pt-20">
          <div className="bg-amber-500 text-black text-center py-2 text-xs sm:text-sm font-bold uppercase tracking-widest" data-testid="test-mode-banner">
            Test &amp; Development — Payments Inactive
          </div>
        </div>
      )}

      {/* Sale banner — scrolling marquee */}
      {saleBanner && (
        <div className={`${!testMode ? 'pt-16 md:pt-20' : ''}`}>
          <div className="relative overflow-hidden py-2.5" style={gradientStyle(saleBanner)} data-testid="sale-banner">
            <div className="sale-marquee flex whitespace-nowrap">
              {[...Array(10)].map((_, i) => (
                <span key={i} className="inline-flex items-center gap-3 mx-6 text-white text-sm font-bold uppercase tracking-wider">
                  <Sparkles className="w-3.5 h-3.5 flex-shrink-0" />
                  {saleBanner.discount_text && <span>{saleBanner.discount_text}</span>}
                  {saleBanner.name && <span className="font-black">{saleBanner.name}</span>}
                  {saleBanner.description && <span className="font-normal opacity-90">{saleBanner.description}</span>}
                </span>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Hero */}
      <section className={`${(testMode || saleBanner) ? 'pt-8 md:pt-12' : 'pt-28 md:pt-36'} pb-12 md:pb-16`}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center gap-3 mb-4">
            <div className="w-10 h-10 rounded-xl bg-[#00E5FF]/10 flex items-center justify-center">
              <ShoppingBag className="w-5 h-5 text-[#00E5FF]" />
            </div>
            <span className="text-xs font-bold uppercase tracking-[0.2em] text-slate-500 dark:text-slate-500">Custom Print Shop</span>
          </div>
          <h1 className="font-['Outfit'] text-4xl sm:text-5xl font-black text-[#0A0A0A] dark:text-white tracking-tighter leading-[1.05]">
            Your Brand,<br />On Everything.
          </h1>
          <p className="mt-4 text-base sm:text-lg text-slate-600 dark:text-slate-400 max-w-xl leading-relaxed">
            Premium branded products customised with your logo. From hoodies to business cards, get your brand out there.
          </p>
        </div>
      </section>

      {/* Filters */}
      <section className="border-b border-slate-200 dark:border-white/5">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 flex gap-2 overflow-x-auto pb-4 scrollbar-hide">
          {categories.map(cat => (
            <button
              key={cat}
              onClick={() => setFilter(cat)}
              data-testid={`filter-${cat}`}
              className={`px-4 py-2 rounded-full text-xs font-medium whitespace-nowrap transition-all ${
                filter === cat
                  ? 'bg-[#0A0A0A] dark:bg-[#8B5CF6] text-white'
                  : 'bg-slate-100 dark:bg-white/5 text-slate-600 dark:text-slate-400 hover:bg-slate-200 dark:hover:bg-violet-400/10'
              }`}
            >
              {cat === 'all' ? 'All Products' : (categoryLabels[cat] || cat)}
            </button>
          ))}
        </div>
      </section>

      {/* Product Grid */}
      <section className="py-12 md:py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {loading ? (
            <div className="text-center py-20">
              <div className="w-8 h-8 border-2 border-[#00E5FF] border-t-transparent rounded-full animate-spin mx-auto mb-4" />
              <p className="text-sm text-slate-500">Loading products...</p>
            </div>
          ) : filtered.length === 0 ? (
            <div className="text-center py-20">
              <Package className="w-10 h-10 text-slate-300 dark:text-slate-600 mx-auto mb-3" />
              <p className="text-sm text-slate-500">No products found</p>
            </div>
          ) : (
            <div className="grid grid-cols-2 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4 md:gap-6">
              {filtered.map(product => (
                <ProductCard key={product.id} product={product} />
              ))}
            </div>
          )}

          {/* Track Order Callout */}
          <div className="mt-12 text-center" data-testid="track-order-callout">
            <p className="text-sm text-slate-500 dark:text-slate-500">
              Already ordered?{' '}
              <Link to="/track" className="text-[#00E5FF] hover:text-white font-medium transition-colors">
                Track your order here
              </Link>
              {' '}to see where we're at.
            </p>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
